import { Box, Tooltip, Flex, Badge, Text } from '@chakra-ui/react';
import { Shipment, ShipmentState } from '@texas/api/endpoints/shipmentsApi';
import { Locale } from '@texas/i18n/types';
import { fadeInScaleAnimation } from '@texas/resources/animations/animations';
import { dateFromNow, formatDate } from '@texas/utils/helpers/dateHelper';
import { compareEnum } from '@texas/utils/helpers/enumHelpers';
import { useTranslation } from 'react-i18next';
import { TransportRequestsSummary } from './TransportRequestsSummary';
import { TexasFormLabel } from '../form/TexasFormLabel';

export function ShipmentItem({
  isSelected,
  shipment,
  onClick,
}: {
  isSelected?: boolean;
  shipment: Shipment;
  onClick?: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Box
      animation={fadeInScaleAnimation()}
      bg="texas.bg.800"
      borderRadius="md"
      boxShadow="md"
      _light={{ bg: 'gray.50' }}
      {...(onClick
        ? {
            onClick: onClick,
            border: '1px solid transparent',
            transition: 'transform 200ms ease, border-color 100ms ease',
            _hover: { transform: 'scale(1.02)' },
            cursor: 'pointer',
          }
        : null)}
      {...(isSelected ? { borderColor: 'gray.300' } : null)}
    >
      <>
        <Flex flexDir="column" gap={3} py={3} px={4}>
          <Flex gap={8}>
            <Box>
              <TransportRequestsSummary
                ticketNumbers={shipment.ticketNumbers ?? []}
                samplesCount={null}
              />
              {compareEnum(
                ShipmentState,
                shipment.state,
                ShipmentState.Dispatched,
              ) ? (
                <Tooltip label={formatDate(Locale.En, shipment.dispatchDate)}>
                  <Badge>
                    {t('shipment.dispatchedDate', {
                      date: dateFromNow(Locale.EnDate, shipment.dispatchDate),
                    })}
                  </Badge>
                </Tooltip>
              ) : (
                <Tooltip label={formatDate(Locale.En, shipment.dispatchDate)}>
                  <Badge>
                    {t('shipment.dispatchesDate', {
                      date: dateFromNow(Locale.EnDate, shipment.dispatchDate),
                    })}
                  </Badge>
                </Tooltip>
              )}
            </Box>
            <Flex flexDir="column" overflow="auto">
              <TexasFormLabel mb={0}>{t('general.to')}</TexasFormLabel>
              <Text noOfLines={1}>{shipment.receiverUser.name}</Text>
              <Text noOfLines={1}>{shipment.receiverUser.email}</Text>
            </Flex>
          </Flex>
        </Flex>
        <Flex
          justifyContent="space-between"
          py={3}
          px={4}
          bg="texas.bg.700"
          borderBottomRadius="md"
          _light={{ bg: 'white' }}
        >
          <Box w="33%">
            <Text fontSize="sm">{t('sample.samples')}</Text>
            <Text fontWeight="bold">{shipment.samplesInShipmentCount}</Text>
          </Box>
          <Box w="33%">
            <Text fontSize="sm">{t('shipment.courier')}</Text>
            <Text fontWeight="bold">{shipment.courier}</Text>
          </Box>
          <Box w="33%">
            <Text fontSize="sm">{t('shipment.trackingNumber')}</Text>
            <Text fontWeight="bold">{shipment.trackingNumber}</Text>
          </Box>
        </Flex>
      </>
    </Box>
  );
}
